/**
 * Pannello admin "Assegnazioni Atleti".
 *
 * La versione precedente usava due Select in cima alla pagina ("seleziona
 * coach per assegnazione diretta" / "seleziona coach team") che però stavano
 * in mezzo ai filtri e non filtravano niente: erano il *bersaglio* delle
 * assegnazioni, e i bottoni "+ Coach" comparivano sulle righe solo dopo aver
 * scelto un valore. Risultato: nessuno capiva dove iniziasse il flusso.
 *
 * Qui il flusso è esplicito e in due varianti:
 *
 * 1. Singolo atleta -> bottone "Gestisci" sulla riga, che apre il picker con
 *    coach e team elencati con email, numero atleti e stato già assegnato.
 *    Un click sulla stessa riga del picker assegna o rimuove.
 * 2. Più atleti -> checkbox sulle righe, barra azioni "Assegna i selezionati",
 *    stesso picker in modalità bulk (mostra quanti dei selezionati hanno già
 *    quel coach/team, così si capisce cosa cambierà davvero).
 *
 * I filtri in cima ora filtrano e basta, incluso un vero filtro per
 * coach/team che prima mancava.
 */
import { useCallback, useMemo, useState } from "react";
import { fetchAPI } from "../../lib/utils";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import {
	Building2,
	Loader2,
	Search,
	UserCog,
	UserMinus,
	UserPlus,
	X,
} from "lucide-react";
import { toast } from "sonner";

const BULK_BATCH_SIZE = 8;

const asList = (value) =>
	Array.isArray(value) ? value.filter(Boolean) : value ? [value] : [];

const coachIdsOf = (athlete) =>
	Array.from(
		new Set([
			...asList(athlete?.coach_ids),
			...(athlete?.coach_id ? [athlete.coach_id] : []),
		]),
	);

const teamIdsOf = (athlete) =>
	Array.from(
		new Set([
			...asList(athlete?.coach_team_ids),
			...(athlete?.team_id ? [athlete.team_id] : []),
		]),
	);

const timestampOf = (dateStr) => {
	if (!dateStr) return 0;
	const ts = new Date(dateStr).getTime();
	return Number.isNaN(ts) ? 0 : ts;
};

export function AthleteAssignmentsTab({
	athletes = [],
	coaches = [],
	teams = [],
	onReload,
	language = "it",
	initialSearch = "",
}) {
	const [search, setSearch] = useState(initialSearch);
	const [sort, setSort] = useState("last_access_desc");
	const [accessFilter, setAccessFilter] = useState("all");
	const [statusFilter, setStatusFilter] = useState("all");
	const [scopeFilter, setScopeFilter] = useState("all");
	const [selectedIds, setSelectedIds] = useState(() => new Set());
	const [picker, setPicker] = useState(null); // { athleteIds: [], bulk: bool }
	const [pickerTab, setPickerTab] = useState("coach");
	const [pickerSearch, setPickerSearch] = useState("");
	const [busy, setBusy] = useState(null); // { key, done, total }

	const formatLastAccess = useCallback(
		(dateStr) => {
			if (!dateStr) return language === "en" ? "Never" : "Mai";
			const date = new Date(dateStr);
			if (Number.isNaN(date.getTime())) return "-";
			return date.toLocaleString(language === "en" ? "en-US" : "it-IT", {
				year: "numeric",
				month: "2-digit",
				day: "2-digit",
				hour: "2-digit",
				minute: "2-digit",
			});
		},
		[language],
	);

	const athletesById = useMemo(() => {
		const map = new Map();
		athletes.forEach((a) => map.set(a.user_id, a));
		return map;
	}, [athletes]);

	const coachById = useMemo(() => {
		const map = new Map();
		coaches.forEach((c) => map.set(c.user_id, c));
		return map;
	}, [coaches]);

	const teamById = useMemo(() => {
		const map = new Map();
		teams.forEach((t) => map.set(t.team_id, t));
		return map;
	}, [teams]);

	const sortedCoaches = useMemo(
		() =>
			[...coaches].sort((a, b) =>
				(a.name || a.email || "").localeCompare(b.name || b.email || ""),
			),
		[coaches],
	);

	const sortedTeams = useMemo(
		() => [...teams].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
		[teams],
	);

	/** Team di appartenenza di un coach: serve come dettaglio nel picker. */
	const teamNamesByCoachId = useMemo(() => {
		const map = new Map();
		teams.forEach((team) => {
			(team.coaches || []).forEach((c) => {
				const list = map.get(c.user_id) || [];
				list.push(team.name);
				map.set(c.user_id, list);
			});
		});
		return map;
	}, [teams]);

	const filteredAthletes = useMemo(() => {
		const q = search.trim().toLowerCase();
		const now = Date.now();
		const recent7 = now - 7 * 24 * 60 * 60 * 1000;
		const recent30 = now - 30 * 24 * 60 * 60 * 1000;

		return athletes
			.filter((athlete) => {
				if (!q) return true;
				return (
					athlete.name?.toLowerCase().includes(q) ||
					athlete.email?.toLowerCase().includes(q)
				);
			})
			.filter((athlete) => {
				if (scopeFilter === "all") return true;
				const [kind, id] = scopeFilter.split(":");
				if (kind === "coach") return coachIdsOf(athlete).includes(id);
				if (kind === "team") return teamIdsOf(athlete).includes(id);
				return true;
			})
			.filter((athlete) => {
				if (statusFilter === "all") return true;
				const assigned =
					coachIdsOf(athlete).length > 0 || teamIdsOf(athlete).length > 0;
				return statusFilter === "assigned" ? assigned : !assigned;
			})
			.filter((athlete) => {
				const ts = timestampOf(athlete.last_access_at);
				if (accessFilter === "all") return true;
				if (accessFilter === "never") return ts === 0;
				if (accessFilter === "recent_7") return ts >= recent7;
				if (accessFilter === "recent_30") return ts >= recent30;
				if (accessFilter === "older_30") return ts > 0 && ts < recent30;
				return true;
			})
			.sort((a, b) => {
				if (sort === "name_asc")
					return (a.name || "").localeCompare(b.name || "");
				if (sort === "name_desc")
					return (b.name || "").localeCompare(a.name || "");
				if (sort === "created_desc")
					return timestampOf(b.created_at) - timestampOf(a.created_at);
				if (sort === "last_access_asc")
					return (
						timestampOf(a.last_access_at) - timestampOf(b.last_access_at)
					);
				return timestampOf(b.last_access_at) - timestampOf(a.last_access_at);
			});
	}, [athletes, search, scopeFilter, statusFilter, accessFilter, sort]);

	const unassignedCount = useMemo(
		() =>
			filteredAthletes.filter(
				(a) => coachIdsOf(a).length === 0 && teamIdsOf(a).length === 0,
			).length,
		[filteredAthletes],
	);

	const visibleSelectedIds = useMemo(
		() => filteredAthletes.filter((a) => selectedIds.has(a.user_id)),
		[filteredAthletes, selectedIds],
	);

	const allVisibleSelected =
		filteredAthletes.length > 0 &&
		visibleSelectedIds.length === filteredAthletes.length;

	const toggleSelect = (athleteId) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(athleteId)) next.delete(athleteId);
			else next.add(athleteId);
			return next;
		});
	};

	const toggleSelectAllVisible = () => {
		setSelectedIds((prev) => {
			if (allVisibleSelected) return new Set();
			const next = new Set(prev);
			filteredAthletes.forEach((a) => next.add(a.user_id));
			return next;
		});
	};

	const pickerAthletes = useMemo(() => {
		if (!picker) return [];
		return picker.athleteIds
			.map((id) => athletesById.get(id))
			.filter(Boolean);
	}, [picker, athletesById]);

	const openSinglePicker = (athlete) => {
		setPickerSearch("");
		setPickerTab("coach");
		setPicker({ athleteIds: [athlete.user_id], bulk: false });
	};

	const openBulkPicker = () => {
		if (!visibleSelectedIds.length) return;
		setPickerSearch("");
		setPickerTab("coach");
		setPicker({
			athleteIds: visibleSelectedIds.map((a) => a.user_id),
			bulk: true,
		});
	};

	/**
	 * Applica add/remove su N atleti a lotti: il backend vuole una PUT per
	 * atleta, ma un reload per atleta renderebbe la UI inservibile. Reload
	 * unico alla fine.
	 */
	const applyAssignment = useCallback(
		async (athleteIds, kind, target, action) => {
			const ids = Array.from(new Set(athleteIds));
			if (!ids.length || busy) return;

			const targetId = kind === "coach" ? target.user_id : target.team_id;
			const endpoint =
				kind === "coach" ? "assign-coach" : "assign-coach-team";
			const body =
				kind === "coach"
					? { coach_id: targetId, action }
					: { team_id: targetId, action };

			setBusy({ key: `${kind}:${targetId}`, done: 0, total: ids.length });
			let ok = 0;
			let failed = 0;

			for (let i = 0; i < ids.length; i += BULK_BATCH_SIZE) {
				const batch = ids.slice(i, i + BULK_BATCH_SIZE);
				const results = await Promise.allSettled(
					batch.map((athleteId) =>
						fetchAPI(`/admin/athletes/${athleteId}/${endpoint}`, {
							method: "PUT",
							body: JSON.stringify(body),
						}),
					),
				);
				results.forEach((r) => {
					if (r.status === "fulfilled") ok += 1;
					else failed += 1;
				});
				setBusy((prev) =>
					prev ? { ...prev, done: Math.min(i + batch.length, ids.length) } : prev,
				);
			}

			setBusy(null);

			const label = target.name || target.email || "-";
			const subject = ok === 1 ? "1 atleta" : `${ok} atleti`;
			if (ok > 0) {
				toast.success(
					action === "add"
						? `${subject} assegnati a ${label}`
						: `${subject} rimossi da ${label}`,
				);
			}
			if (failed > 0) {
				toast.error(
					`${failed} assegnazioni non riuscite su ${label}. Riprova.`,
				);
			}

			await onReload?.();
		},
		[busy, onReload],
	);

	const isBusyFor = (kind, id) => busy?.key === `${kind}:${id}`;

	const pickerRows = useMemo(() => {
		const q = pickerSearch.trim().toLowerCase();
		const matches = (text) => !q || (text || "").toLowerCase().includes(q);

		if (pickerTab === "coach") {
			return sortedCoaches
				.filter(
					(coach) =>
						matches(coach.name) ||
						matches(coach.email) ||
						(coach.disciplines || []).some((d) => matches(d)),
				)
				.map((coach) => {
					const assigned = pickerAthletes.filter((a) =>
						coachIdsOf(a).includes(coach.user_id),
					).length;
					return { kind: "coach", id: coach.user_id, target: coach, assigned };
				});
		}

		return sortedTeams
			.filter(
				(team) =>
					matches(team.name) ||
					(team.coaches || []).some(
						(c) => matches(c.name) || matches(c.email),
					),
			)
			.map((team) => {
				const assigned = pickerAthletes.filter((a) =>
					teamIdsOf(a).includes(team.team_id),
				).length;
				return { kind: "team", id: team.team_id, target: team, assigned };
			});
	}, [pickerTab, pickerSearch, sortedCoaches, sortedTeams, pickerAthletes]);

	const pickerTotal = pickerAthletes.length;
	const pickerTitle = picker?.bulk
		? `Assegna ${pickerTotal} atleti`
		: `Assegnazioni di ${pickerAthletes[0]?.name || ""}`;

	return (
		<>
			{/* Filtri: qui dentro ci sono solo cose che filtrano la lista. */}
			<Card className="bg-card border-border rounded-sm mb-4">
				<CardContent className="pt-6">
					<div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wider text-muted-foreground">
						<Search className="w-3.5 h-3.5" />
						Filtra la lista atleti
					</div>
					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
						<Input
							placeholder="Cerca atleta (nome o email)..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className="bg-secondary border-transparent rounded-sm"
						/>
						<Select value={scopeFilter} onValueChange={setScopeFilter}>
							<SelectTrigger className="bg-secondary border-transparent rounded-sm">
								<SelectValue placeholder="Coach o team" />
							</SelectTrigger>
							<SelectContent className="bg-card border-border">
								<SelectItem value="all">Tutti i coach e i team</SelectItem>
								{sortedCoaches.length > 0 && (
									<SelectGroup>
										<SelectLabel>Coach diretti</SelectLabel>
										{sortedCoaches.map((coach) => (
											<SelectItem
												key={coach.user_id}
												value={`coach:${coach.user_id}`}
											>
												{coach.name || coach.email}
												{coach.email ? ` · ${coach.email}` : ""}
											</SelectItem>
										))}
									</SelectGroup>
								)}
								{sortedTeams.length > 0 && (
									<SelectGroup>
										<SelectLabel>Coach team</SelectLabel>
										{sortedTeams.map((team) => (
											<SelectItem
												key={team.team_id}
												value={`team:${team.team_id}`}
											>
												{team.name}
											</SelectItem>
										))}
									</SelectGroup>
								)}
							</SelectContent>
						</Select>
						<Select value={statusFilter} onValueChange={setStatusFilter}>
							<SelectTrigger className="bg-secondary border-transparent rounded-sm">
								<SelectValue placeholder="Stato assegnazione" />
							</SelectTrigger>
							<SelectContent className="bg-card border-border">
								<SelectItem value="all">Assegnati e non</SelectItem>
								<SelectItem value="assigned">Con assegnazioni</SelectItem>
								<SelectItem value="unassigned">Senza assegnazioni</SelectItem>
							</SelectContent>
						</Select>
						<Select value={accessFilter} onValueChange={setAccessFilter}>
							<SelectTrigger className="bg-secondary border-transparent rounded-sm">
								<SelectValue placeholder="Filtro accesso" />
							</SelectTrigger>
							<SelectContent className="bg-card border-border">
								<SelectItem value="all">Tutti gli accessi</SelectItem>
								<SelectItem value="recent_7">
									Attivi negli ultimi 7 giorni
								</SelectItem>
								<SelectItem value="recent_30">
									Attivi negli ultimi 30 giorni
								</SelectItem>
								<SelectItem value="older_30">
									Inattivi da oltre 30 giorni
								</SelectItem>
								<SelectItem value="never">Mai entrati</SelectItem>
							</SelectContent>
						</Select>
						<Select value={sort} onValueChange={setSort}>
							<SelectTrigger className="bg-secondary border-transparent rounded-sm">
								<SelectValue placeholder="Ordinamento" />
							</SelectTrigger>
							<SelectContent className="bg-card border-border">
								<SelectItem value="last_access_desc">
									Ultimo accesso (piu recenti)
								</SelectItem>
								<SelectItem value="last_access_asc">
									Ultimo accesso (meno recenti)
								</SelectItem>
								<SelectItem value="name_asc">Nome (A-Z)</SelectItem>
								<SelectItem value="name_desc">Nome (Z-A)</SelectItem>
								<SelectItem value="created_desc">
									Registrazione piu recente
								</SelectItem>
							</SelectContent>
						</Select>
					</div>
				</CardContent>
			</Card>

			<Card className="bg-card border-border rounded-sm">
				<CardHeader className="space-y-3">
					<div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-2">
						<div>
							<CardTitle className="font-heading text-xl flex items-center gap-2">
								<UserCog className="w-5 h-5 text-volt" />
								Assegnazioni Atleti
							</CardTitle>
							<p className="text-xs text-muted-foreground mt-1 max-w-xl">
								<strong>Gestisci</strong> su una riga per assegnare un singolo
								atleta. Oppure spunta più atleti e usa l'assegnazione multipla.
								Il <strong>coach diretto</strong> segue l'atleta da solo, il{" "}
								<strong>coach team</strong> lo rende visibile a tutti i coach di
								quel team.
							</p>
						</div>
						<p className="text-xs text-muted-foreground shrink-0 tabular-nums">
							{filteredAthletes.length} atleti in elenco · {unassignedCount}{" "}
							senza assegnazione
						</p>
					</div>

					<div className="flex flex-wrap items-center gap-3">
						<div className="flex items-center gap-2 text-xs text-muted-foreground">
							<Checkbox
								checked={allVisibleSelected}
								disabled={!filteredAthletes.length}
								onClick={toggleSelectAllVisible}
								aria-label="Seleziona tutti gli atleti filtrati"
							/>
							<button
								type="button"
								disabled={!filteredAthletes.length}
								onClick={toggleSelectAllVisible}
								className="hover:text-foreground transition-colors disabled:opacity-50"
							>
								Seleziona tutti ({filteredAthletes.length})
							</button>
						</div>
					</div>
				</CardHeader>

				{visibleSelectedIds.length > 0 && (
					<div className="mx-6 mb-3 rounded-sm border border-volt/40 bg-volt/5 p-3">
						<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
							<p className="text-sm">
								<span className="font-medium text-volt">
									{visibleSelectedIds.length} atleti selezionati
								</span>
							</p>
							<div className="flex flex-wrap items-center gap-2">
								<Button
									size="sm"
									className="rounded-sm bg-volt text-black hover:bg-volt/90"
									onClick={openBulkPicker}
								>
									<UserPlus className="w-4 h-4 mr-2" />
									Assegna i selezionati
								</Button>
								<Button
									variant="ghost"
									size="sm"
									className="rounded-sm"
									onClick={() => setSelectedIds(new Set())}
								>
									<X className="w-4 h-4 mr-1" />
									Deseleziona
								</Button>
							</div>
						</div>
					</div>
				)}

				<CardContent>
					{/* overflow-y-auto e non ScrollArea: il viewport di Radix usa
					    display:table, quindi min-w-0/truncate non vincolano i figli e
					    le righe sbordano a destra invece di troncare. */}
					<div className="h-[520px] overflow-y-auto overflow-x-hidden pr-2">
						<div className="space-y-3">
							{filteredAthletes.map((athlete) => {
								const selected = selectedIds.has(athlete.user_id);
								const assignedCoaches = coachIdsOf(athlete)
									.map((id) => coachById.get(id))
									.filter(Boolean);
								const assignedTeams = teamIdsOf(athlete)
									.map((id) => teamById.get(id))
									.filter(Boolean);

								return (
									<div
										key={athlete.user_id}
										className={`p-4 rounded-sm transition-colors ${
											selected ? "bg-volt/10 ring-1 ring-volt/40" : "bg-secondary"
										}`}
									>
										<div className="flex items-start gap-3">
											<Checkbox
												checked={selected}
												className="mt-1 shrink-0"
												aria-label={`Seleziona ${athlete.name}`}
												onClick={() => toggleSelect(athlete.user_id)}
											/>
											<div className="min-w-0 flex-1">
												<div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-3">
													<div className="min-w-0">
														<p className="font-medium truncate">
															{athlete.name}
														</p>
														<p className="text-xs text-muted-foreground truncate">
															{athlete.email}
														</p>
														<p className="text-xs text-muted-foreground/80">
															{language === "en"
																? "Last access"
																: "Ultimo accesso"}
															: {formatLastAccess(athlete.last_access_at)}
														</p>
													</div>
													<Button
														variant="outline"
														size="sm"
														className="rounded-sm shrink-0 border-volt/50 text-volt hover:bg-volt/10"
														onClick={() => openSinglePicker(athlete)}
													>
														<UserCog className="w-4 h-4 mr-2" />
														Gestisci assegnazioni
													</Button>
												</div>

												<div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
													<div className="space-y-2">
														<p className="text-xs uppercase tracking-wider text-muted-foreground">
															Coach diretti
														</p>
														{assignedCoaches.length > 0 ? (
															<div className="flex flex-wrap gap-2">
																{assignedCoaches.map((coach) => (
																	<div
																		key={coach.user_id}
																		className="flex items-center gap-2 px-2 py-1 bg-card rounded-sm text-xs"
																		title={coach.email}
																	>
																		<span>{coach.name || coach.email}</span>
																		<button
																			type="button"
																			aria-label={`Rimuovi ${coach.name}`}
																			onClick={() =>
																				applyAssignment(
																					[athlete.user_id],
																					"coach",
																					coach,
																					"remove",
																				)
																			}
																			className="text-muted-foreground hover:text-blaze"
																		>
																			<X className="w-3.5 h-3.5" />
																		</button>
																	</div>
																))}
															</div>
														) : (
															<p className="text-xs text-muted-foreground">
																Nessuna assegnazione diretta
															</p>
														)}
													</div>

													<div className="space-y-2">
														<p className="text-xs uppercase tracking-wider text-muted-foreground">
															Coach Team
														</p>
														{assignedTeams.length > 0 ? (
															<div className="flex flex-wrap gap-2">
																{assignedTeams.map((team) => (
																	<div
																		key={team.team_id}
																		className="flex items-center gap-2 px-2 py-1 bg-card rounded-sm text-xs"
																		title={`${team.coach_count || 0} coach`}
																	>
																		<span>{team.name}</span>
																		<button
																			type="button"
																			aria-label={`Rimuovi ${team.name}`}
																			onClick={() =>
																				applyAssignment(
																					[athlete.user_id],
																					"team",
																					team,
																					"remove",
																				)
																			}
																			className="text-muted-foreground hover:text-blaze"
																		>
																			<X className="w-3.5 h-3.5" />
																		</button>
																	</div>
																))}
															</div>
														) : (
															<p className="text-xs text-muted-foreground">
																Nessuna assegnazione team
															</p>
														)}
													</div>
												</div>
											</div>
										</div>
									</div>
								);
							})}
							{filteredAthletes.length === 0 && (
								<p className="text-sm text-muted-foreground text-center py-8">
									Nessun atleta trovato con i filtri correnti
								</p>
							)}
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Picker: stesso componente per singolo e bulk. */}
			<Dialog
				open={!!picker}
				onOpenChange={(open) => {
					if (!open && !busy) setPicker(null);
				}}
			>
				<DialogContent className="bg-card border-border max-w-2xl">
					<DialogHeader>
						<DialogTitle className="font-heading">{pickerTitle}</DialogTitle>
						<DialogDescription>
							{picker?.bulk
								? "Le azioni valgono per tutti gli atleti selezionati. Accanto a ogni voce vedi quanti li hanno già."
								: pickerAthletes[0]?.email}
						</DialogDescription>
					</DialogHeader>

					<Tabs value={pickerTab} onValueChange={setPickerTab}>
						<TabsList className="bg-secondary rounded-sm">
							<TabsTrigger value="coach" className="rounded-sm">
								<UserCog className="w-4 h-4 mr-2" />
								Coach diretti
							</TabsTrigger>
							<TabsTrigger value="team" className="rounded-sm">
								<Building2 className="w-4 h-4 mr-2" />
								Coach team
							</TabsTrigger>
						</TabsList>
					</Tabs>

					<Input
						placeholder={
							pickerTab === "coach"
								? "Cerca coach per nome, email o disciplina..."
								: "Cerca team per nome o coach..."
						}
						value={pickerSearch}
						onChange={(e) => setPickerSearch(e.target.value)}
						className="bg-secondary border-transparent rounded-sm"
					/>

					<div className="max-h-[340px] overflow-y-auto overflow-x-hidden pr-2">
						<div className="space-y-2">
							{pickerRows.map((row) => {
								const { kind, id, target, assigned } = row;
								const rowBusy = isBusyFor(kind, id);
								const allAssigned = pickerTotal > 0 && assigned === pickerTotal;
								const meta =
									kind === "coach"
										? [
												`${target.athletes?.length || 0} atleti`,
												...(target.disciplines?.length
													? [target.disciplines.join(", ")]
													: []),
												...(teamNamesByCoachId.get(target.user_id)?.length
													? [
															`Team: ${teamNamesByCoachId
																.get(target.user_id)
																.join(", ")}`,
														]
													: []),
											]
										: [
												`${target.coach_count || 0} coach`,
												`${target.athlete_count || 0} atleti`,
												...((target.coaches || []).length
													? [
															target.coaches
																.map((c) => c.name || c.email)
																.join(", "),
														]
													: []),
											];

								return (
									<div
										key={id}
										className={`flex items-start justify-between gap-3 p-3 rounded-sm ${
											allAssigned ? "bg-volt/10 ring-1 ring-volt/30" : "bg-secondary"
										}`}
									>
										<div className="min-w-0 flex-1">
											<div className="flex items-center gap-2 flex-wrap">
												<p className="font-medium break-words">
													{target.name || target.email}
												</p>
												{kind === "coach" && target.is_admin && (
													<Badge className="border-0 text-[10px] bg-acid/20 text-acid">
														Admin
													</Badge>
												)}
												{kind === "coach" && target.is_active === false && (
													<Badge className="border-0 text-[10px] bg-destructive/20 text-destructive">
														Disattivato
													</Badge>
												)}
												{allAssigned && (
													<Badge className="border-0 text-[10px] bg-volt/20 text-volt">
														Assegnato
													</Badge>
												)}
												{!allAssigned && assigned > 0 && (
													<Badge className="border-0 text-[10px] bg-secondary text-muted-foreground">
														{assigned}/{pickerTotal} già assegnati
													</Badge>
												)}
											</div>
											{kind === "coach" && target.email && (
												<p className="text-xs text-muted-foreground break-words">
													{target.email}
												</p>
											)}
											<p className="text-xs text-muted-foreground/80 break-words">
												{meta.join(" · ")}
											</p>
										</div>

										<div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
											{rowBusy && (
												<span className="flex items-center gap-1.5 text-xs text-volt tabular-nums">
													<Loader2 className="w-3.5 h-3.5 animate-spin" />
													{busy.done}/{busy.total}
												</span>
											)}
											{assigned < pickerTotal && (
												<Button
													size="sm"
													className="rounded-sm bg-volt text-black hover:bg-volt/90"
													disabled={!!busy}
													onClick={() =>
														applyAssignment(
															picker.athleteIds,
															kind,
															target,
															"add",
														)
													}
												>
													<UserPlus className="w-4 h-4 mr-2" />
													{picker?.bulk
														? `Assegna (${pickerTotal - assigned})`
														: "Assegna"}
												</Button>
											)}
											{assigned > 0 && (
												<Button
													variant="outline"
													size="sm"
													className="rounded-sm border-destructive/40 text-destructive hover:bg-destructive/10"
													disabled={!!busy}
													onClick={() =>
														applyAssignment(
															picker.athleteIds,
															kind,
															target,
															"remove",
														)
													}
												>
													<UserMinus className="w-4 h-4 mr-2" />
													{picker?.bulk ? `Rimuovi (${assigned})` : "Rimuovi"}
												</Button>
											)}
										</div>
									</div>
								);
							})}
							{pickerRows.length === 0 && (
								<p className="text-sm text-muted-foreground text-center py-8">
									{pickerTab === "coach"
										? "Nessun coach trovato"
										: "Nessun coach team trovato"}
								</p>
							)}
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
}
